import React, { useState } from 'react';
import { 
    Box, Typography, Container, Card, CardContent, 
    Grid, Tabs, Tab, Button, useTheme, Chip, Paper, 
    Divider, LinearProgress, Table, TableBody, TableCell, 
    TableHead, TableRow, IconButton, Tooltip, Alert
} from '@mui/material';
import { 
    Policy as PolicyIcon, 
    Gavel as GavelIcon, 
    TrackChanges as TrackChangesIcon,
    VerifiedUser as VerifiedUserIcon,
    DataObject as DataObjectIcon,
    Visibility as VisibilityIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    ErrorOutline as ErrorOutlineIcon,
    Update as UpdateIcon
} from '@mui/icons-material';

// --- MOCK DATA ---

// Compliance Status Data
const complianceStatus = [
    { rule: 'GDPR (EU)', status: 'Compliant', score: 95, icon: VerifiedUserIcon, color: 'success', lastAudit: '2025-09-01' },
    { rule: 'CCPA (CA)', status: 'Compliant', score: 90, icon: VerifiedUserIcon, color: 'success', lastAudit: '2025-09-15' },
    { rule: 'HIPAA (US)', status: 'Needs Review', score: 65, icon: ErrorOutlineIcon, color: 'warning', lastAudit: '2025-08-01' },
    { rule: 'PIA (Global)', status: 'In Progress', score: 40, icon: UpdateIcon, color: 'info', lastAudit: '2025-10-01' },
];

// Data Subject Access Requests (DSAR) Tracking
const dsarRequests = [
    { id: 'DSAR-001', type: 'Access Request', subject: 'John Doe', status: 'Pending Review', date: '2025-10-01', action: 'Review' },
    { id: 'DSAR-002', type: 'Erasure (Right to be Forgotten)', subject: 'Jane Smith', status: 'In Progress', date: '2025-09-25', action: 'Process' },
    { id: 'DSAR-003', type: 'Data Portability', subject: 'Mike Lee', status: 'Completed', date: '2025-09-10', action: 'View Log' },
];

// Policy Document Links
const policies = [
    { name: 'Global Data Protection Policy v3.1', date: '2025-09-01', link: '#', icon: PolicyIcon },
    { name: 'CCPA-Specific Addendum', date: '2025-09-15', link: '#', icon: PolicyIcon },
    { name: 'Sub-Processor List', date: '2025-10-05', link: '#', icon: DataObjectIcon },
];

// Tab Panel Component
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`compliance-tabpanel-${index}`}
            aria-labelledby={`compliance-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

// --- MAIN COMPONENT ---
const DataPrivacyCompliance = () => {
    const [currentTab, setCurrentTab] = useState(0);
    const theme = useTheme();

    const handleChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.main, 
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <GavelIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.main }} />
                Data Privacy & Compliance Center
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Manage data governance, audit compliance, and handle data subject requests.
            </Typography>

            <Paper elevation={6} sx={{ p: 3, borderRadius: 2 }}>
                
                <Tabs 
                    value={currentTab} 
                    onChange={handleChange} 
                    indicatorColor="secondary" 
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: `2px solid ${theme.palette.divider}` }}
                >
                    <Tab label={<Box display="flex" alignItems="center"><GavelIcon sx={{ mr: 1 }} /> Compliance Status</Box>} />
                    <Tab label={<Box display="flex" alignItems="center"><TrackChangesIcon sx={{ mr: 1 }} /> DSAR Tracking</Box>} />
                    <Tab label={<Box display="flex" alignItems="center"><PolicyIcon sx={{ mr: 1 }} /> Policy Repository</Box>} />
                </Tabs>

                {/* --- TAB 1: COMPLIANCE STATUS --- */}
                <TabPanel value={currentTab} index={0}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        The overall compliance score is **82%**. Please review the HIPAA status immediately.
                    </Alert>
                    
                    <Grid container spacing={3}>
                        {complianceStatus.map((item, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card 
                                    elevation={3} 
                                    sx={{ 
                                        height: '100%', 
                                        borderLeft: `5px solid ${theme.palette[item.color].main}`,
                                        transition: '0.3s',
                                        '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[6] }
                                    }}
                                >
                                    <CardContent>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                            <Typography variant="subtitle1" fontWeight="bold">{item.rule}</Typography>
                                            <Chip 
                                                label={item.status} 
                                                color={item.color} 
                                                size="small" 
                                                icon={item.icon ? <item.icon /> : null}
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </Box>
                                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                                            {item.score}% Compliant
                                        </Typography>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={item.score} 
                                            color={item.color} 
                                            sx={{ height: 10, borderRadius: 5, mb: 1 }}
                                        />
                                        <Typography variant="caption" color="textSecondary">
                                            Last Audit: {item.lastAudit}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    
                    <Divider sx={{ my: 4 }} />
                    <Box display="flex" justifyContent="flex-end">
                        <Button variant="contained" color="secondary" startIcon={<UpdateIcon />}>
                            Run New Compliance Scan
                        </Button>
                    </Box>
                </TabPanel>

                {/* --- TAB 2: DSAR TRACKING --- */}
                <TabPanel value={currentTab} index={1}>
                    <Typography variant="h5" gutterBottom fontWeight={600} mb={2}>
                        Data Subject Access Requests (DSAR)
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        There are **2 active requests** requiring action to meet the 30-day response deadline.
                    </Alert>

                    <Paper variant="outlined">
                        <Table size="medium">
                            <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Request ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Data Subject</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date Filed</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dsarRequests.map((request) => (
                                    <TableRow key={request.id}>
                                        <TableCell>{request.id}</TableCell>
                                        <TableCell>{request.type}</TableCell>
                                        <TableCell>{request.subject}</TableCell>
                                        <TableCell>{request.date}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={request.status} 
                                                color={request.status === 'Completed' ? 'success' : (request.status.includes('Pending') ? 'error' : 'info')}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" variant="outlined" color="primary">
                                                {request.action}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </TabPanel>

                {/* --- TAB 3: POLICY REPOSITORY --- */}
                <TabPanel value={currentTab} index={2}>
                    <Typography variant="h5" gutterBottom fontWeight={600} mb={2}>
                        Official Compliance Documents
                    </Typography>

                    <Grid container spacing={2}>
                        {policies.map((policy, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card elevation={1}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                                        <policy.icon color="action" sx={{ fontSize: 40, mr: 2 }} />
                                        <Box flexGrow={1}>
                                            <Typography variant="subtitle1" fontWeight="bold">{policy.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">Last Updated: {policy.date}</Typography>
                                        </Box>
                                        <Tooltip title="View Document">
                                            <IconButton color="primary" href={policy.link} target="_blank">
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </TabPanel>

            </Paper>
        </Container>
    );
};

// EXPORT STATEMENT IS CORRECT
export default DataPrivacyCompliance;